import { SidebarInset, SidebarProvider } from "@pdfking/components"
import type { ObjectForm } from "@pdfking/forms"
import { CreateReactiveForm } from "@pdfking/forms"
import type { DestinationID, ImposeResult } from "@pdfking/imposition"
import { JobDestinationList, LayerList } from "@pdfking/imposition"
import type { CustomConfig, FileSlot } from "@pdfking/imposition-form"
import { FileSlotPage } from "@pdfking/imposition-form"
import { Round } from "@pdfking/math"
import type { FilePlottingContext, PdfEngine, SinglePagePdf, VirtualFile } from "@pdfking/pdfengine"
import { CreateRemoteFile, FileContentGUID } from "@pdfking/pdfengine"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { PlottingContext } from "@pdfking/renderer-api"
import { CreateContext } from "@pdfking/renderer-api"
import type { PageNumber } from "@pdfking/renderer-api"
import type { CacheBackend } from "@pdfking/storages"
import type { FileName } from "@pdfking/structs"
import { NullQ, StringQ } from "@pdfking/structs"
import type { ContentRequest } from "@pdfking/templates-content"
import type { StringGUID } from "@pdfking/tfunc"
import {
  GuidToString,
  Items,
  JSONEncode,
  KeyTake,
  Keys,
  Length,
  RecordMap,
  RecordMerge,
  Values,
  Void,
  WaitPromises,
} from "@pdfking/tfunc"
import type { ComponentChildren, FunctionComponent } from "preact"
import { useContext } from "preact/hooks"

import type { CameraMode } from "#public"
import type { PdfviewRouterType, PdfviewState } from "#public"
import type {
  Guide,
  GuideId,
  PageCount,
  Preset,
  RequestMedia,
  ResultContext,
  SlotMedia,
} from "#public"
import { GeneratedSlots, MARKS_GUIDE } from "#public"

import { Book3dViewer } from "../components/Book3dViewer"
import { PageGroupingControl } from "../components/PageGroupingControl"
import { ToggleButton } from "../components/ToggleButton"
import { UploadCard } from "../components/UploadCard"
import { ViewerPanels } from "../components/ViewerPanels"
import { ViewerRail } from "../components/ViewerRail"
import { ViewerSidebar } from "../components/ViewerSidebar"
import { CreatePresetController } from "../controller/CreatePresetController"
import { GuideVisible } from "../controller/GuideVisible"
import { ResultPages } from "../controller/ResultView"
import { ShowDestinations } from "../controller/ShowDestinations"
import { DetectLang } from "../i18n/Languages"
import { set_language, useTranslationTyped } from "../i18n/useTranslation"
import { IDMixin } from "../mixins/IDMixin"
import { RootMixin } from "../mixins/RootMixin"
import { MediaDefs } from "../plot/MediaDefs"
import { PlotViewer } from "../plot/PlotViewer"
import { ResolvePreset } from "../presets/ResolvePreset"
import { CreateFileCollectionStore } from "../store/CreateFileCollectionStore"
import { CreateMediaImages } from "../store/CreateMediaImages"
import { NewId } from "../utils/Counters"
import { IngestFiles } from "../utils/IngestFiles"
import { PdfviewRouter } from "./PdfviewRouter"
import { PdfviewStateDefaults } from "./PdfviewStateDefaults"

const HAS_MARKS = Values(LayerList).some(layer => layer.printable)

/** One guide per destination the result prints, and the marks. */
const result_guides = (result: ImposeResult): Guide[] => [
  ...(Keys(result.destinations) as DestinationID[]).map(id => ({
    id: id as GuideId,
    name: JobDestinationList[id]?.name ?? id,
    kind: "destination" as const,
    active: true,
  })),
  ...(HAS_MARKS ? [{ id: MARKS_GUIDE, name: "MARKS", kind: "marks" as const, active: false }] : []),
]

/**
 * What the header's action button is handed when it renders — everything a
 * host needs to act on the current document without reaching into the viewer.
 *
 * The two payloads are NOT interchangeable, and picking the wrong one is a
 * runtime error on the far end. `state.imposition_data` is the form's raw
 * *input*: what the user typed, holes and all (an untouched optional field is
 * `null` there), which is exactly right for the route — it round-trips a
 * half-filled form through a reload. `data` is the same form's validated
 * *value*: defaults applied, measures normalized, empty fields dropped. That
 * is the one a backend can parse (rotostampa's `ImpositionInput(**data)`
 * rejects a `null` outright, with "Value cannot be None").
 */
export type PdfviewAction = {
  data: Record<string, unknown>
  state: PdfviewState
  /** What the form imposes; none while it doesn't validate. */
  result: ImposeResult | undefined
}

export type PdfViewerOptions = {
  engine: PdfEngine
  // The engine's own response cache, if the host kept a reference to it
  // (see e.g. apps/frontend's EngineCache) — CreateEngine closes over
  // it internally, so PdfEngine itself never exposes it. Threaded down to
  // the debug view's "nuke cache" button; omit to leave that cache alone.
  cache?: Pick<CacheBackend<unknown>, "clear">
  /**
   * Show the developer affordances: the Debug panel (route state + file store)
   * and the "delete cache" button. Off unless the host asks for it — an end
   * user has no use for either, and the cache button in particular is a foot-gun
   * in their hands. The standalone app turns it on outright; rotostampa turns it
   * on for staff.
   */
  debug?: boolean
  /** Preset used when the incoming state doesn't carry one. */
  default_preset?: string
  /** The top bar (pager, zoom, guides, action). Off for an inline preview. */
  show_header?: boolean
  /**
   * The icon rail and its panels. Off for an inline preview. Not `sidebar`:
   * that's the route field naming the open panel (see PdfviewState).
   */
  show_sidebar?: boolean
  /**
   * The presets the picker offers and the form is built from: the
   * imposition's own `PresetList`, or a host's list built on it (see
   * `@pdfking/pdfview-roto`), which brings its own markings and materials.
   */
  presets: Record<string, Preset>
  /**
   * The language every translated string (and the form's baked-in choice
   * labels) is rendered in — a getter, not a value, because it's dispatched
   * down to components: read inside a computed, so a host backing it with a
   * signal re-renders and rebuilds the form on a switch, with no route field
   * and no prop threading. Language is the host's state (as is dark mode,
   * which the viewer no longer touches at all — set the `dark` class
   * yourself), not the document's, so neither one round-trips through
   * {@link PdfviewState}. Defaults to the browser's language.
   */
  lang?: () => string
  sidebar_width?: string
  /**
   * The icon rail the sidebar hangs off: {@link ViewerRail}, or a host's own
   * with the same two slots (its switches around them). The viewer fills
   * `top` with its panels and `bottom` with its debug views and the host's
   * `children`.
   */
  rail?: FunctionComponent<{ top: ComponentChildren; bottom: ComponentChildren }>
  /**
   * Size suggestions the form's size fields autocomplete against — the host's
   * own catalog (`imposition_config` in filecheck.tsx pulls this from the
   * backend). Materials come with the presets.
   */
  form_config?: Omit<CustomConfig, "language">
  /**
   * The header's primary action, rendered at the right end next to the 3D
   * toggle — the whole button, not just its handler. Receives the current
   * document as a {@link PdfviewAction}, so the host owns both what the action
   * does (plot client-side, POST somewhere, reuse a link already on the page)
   * and how it reads.
   *
   * The button is the host's because its label is: the same viewer is opened
   * to save an imposition back to an order, to send a proof for approval, or
   * to just download a template, and each of those wants its own wording (the
   * backend routes one per entry point — see `filecheck_url(submit_button=…)`
   * in products/prints/links.py). A fixed "Download" with a label override
   * would still be wrong for the ones that also want a different icon,
   * variant, or a disabled state of their own. Omit it and the header simply
   * carries no action.
   */
  action_button?: (action: PdfviewAction) => ComponentChildren
  /**
   * A generated page as a file of the engine's (see
   * `@pdfking/templates-content`'s CreateContentFile): laid out, plotted and
   * rasterized in the background like any page, only when a slot is shown or
   * plotted. Without it a slot with no file, and a coating's film, show
   * nothing.
   */
  generate_content?: (request: ContentRequest) => Promise<SinglePagePdf>
}

/**
 * Build the self-contained viewer app (sidebar + header controls + plot) for
 * a given engine. State comes in as props ({@link PdfviewState}) and every
 * change goes out through the {@link PdfviewRouter} context, so the host owns
 * persistence — pair it with any router by mapping `navigate` onto a route.
 * `children` are the host's controls, at the foot of the rail.
 */
export const CreatePdfViewer = ({
  engine,
  cache,
  action_button,
  generate_content,
  debug = false,
  default_preset = "file",
  show_header = true,
  lang = DetectLang,
  form_config,
  presets,
  rail: Rail = ViewerRail,
  show_sidebar = true,
  sidebar_width = "43rem",
}: PdfViewerOptions) => {
  // URL-driven: every control change emits a patch of just the fields it
  // touched — `navigate` for document-changing actions (form edits, preset
  // swaps: one history entry each, so back = undo), `replace` for view state
  // (page, zoom, sidebar, toggles) — the host merges it into its route, and
  // the props are decoded from the incoming route. The `imposition_data`
  // watcher is the only writer into the form node, the `encoded_input`
  // watcher the only path pushing form commits back out — each side skips
  // when the other already holds the data, so pushes and their route echoes
  // never cycle.
  return CreateRenderFunction({
    computed: {
      // Wrapping the host's getter in a computed is what makes it reactive:
      // whatever signal it reads is tracked here, so a language switch
      // re-renders and fires the `lang` watcher (form rebuild) on its own.
      lang(): string {
        return lang()
      },
      controller() {
        return CreatePresetController(this.node!, this.files_node!, this.preset!, this.store)
      },
      result(): ImposeResult | undefined {
        return this.node ? this.controller.result : undefined
      },
      pages() {
        return this.result ? ResultPages(this.result) : []
      },
      guides(): Guide[] {
        return this.result ? result_guides(this.result) : []
      },
      // What the form panels read off the result. A packed imposition only
      // plots the destinations it laid out, so a row may only target those; the
      // pass-through derives its destinations from the rows, so it offers all.
      result_view(): ResultContext | undefined {
        const result = this.result
        if (!result) return undefined
        return {
          // A coating (lamination) takes no file: never a row's choice.
          file_destinations: new Set(
            (
              Keys(
                this.preset?.impose ? result.destinations : JobDestinationList
              ) as DestinationID[]
            ).filter(id => JobDestinationList[id]?.print_mode !== "coating")
          ),
          guides: this.guides,
          sample_media: result.pages[1 as PageNumber],
        }
      },
      active_guides(): Guide[] {
        return this.guides.filter((g: Guide) => GuideVisible(g, this.hidden_guides))
      },
      // Whether the marks guide is active — the flat plot switches from guide
      // preview colors (e.g. UV's blue highlight) to real print colors once it is.
      printable(): boolean {
        const marks = this.guides.find((g: Guide) => g.kind === "marks")
        return marks ? GuideVisible(marks, this.hidden_guides) : false
      },
      fields() {
        return this.result?.fields ?? []
      },
      informations() {
        return this.result?.informations ?? []
      },
      // The sheets math, for staff only.
      sheet_math() {
        return debug ? (this.result?.sheet_math ?? []) : []
      },
      // Per destination a file can print on, the pages the imposition gives it
      // against the pages the file rows select for it. The pass-through takes
      // its pages from the rows, so they always agree.
      page_counts(): PageCount[] {
        const result = this.result
        if (!result || !this.preset?.impose) return []
        return (Items(result.destinations) as [DestinationID, PageNumber[]][])
          .filter(([id]) => JobDestinationList[id]?.print_mode !== "coating")
          .map(([id, pages]) => ({
            name: JobDestinationList[id]?.name ?? id,
            expected: pages.length,
            selected: Length(this.controller.file_map[id] ?? {}),
          }))
      },
      // The slice of `store` (the single source of truth for what's loaded)
      // that form-commit pushes persist alongside `imposition_data`.
      loaded_files(): Record<FileName, VirtualFile> {
        const result: Record<FileName, VirtualFile> = {}
        for (const name of Keys(this.store.files) as FileName[]) {
          result[name] = this.store.files[name].file
        }
        return result
      },
      // Colors, layers and the unit scale. No files: they stream in page by
      // page, and every shape measures with this context — each media reads
      // its slot and asks for its own raster through `request_media`.
      plot_context(): PlottingContext<unknown> {
        return CreateContext(
          RecordMerge(engine.config, { color_map: this.result!.color_map, layer_map: LayerList })
        )
      },
      // Which page fills each destination slot: the uploads', as the file rows
      // bind them, and the generated book's where no upload is (see
      // GeneratedSlots), seeded by the viewer's uuid, a book of its own for
      // every viewer
      slot_map(): Record<DestinationID, Record<PageNumber, FileSlot>> {
        const uploads = this.controller.file_map
        const result = this.result
        if (!generate_content || !result) return uploads
        return RecordMerge(
          uploads,
          GeneratedSlots({
            content: this.content,
            filename: destination => destination as string as FileName,
            generate: { bleed: engine.config.file_bleed, file: generate_content },
            has_files: Length(this.store.files) > 0,
            language: this.lang,
            result,
            seed: this.uuid,
          })
        )
      },
      encoded_input(): string {
        return this.node ? JSONEncode(this.controller.input) : "{}"
      },
      // The validated counterpart of `imposition_data` — see PdfviewAction for
      // why the two are kept apart. Empty until the form exists.
      form_data(): Record<string, unknown> {
        return this.node ? this.controller.data : {}
      },
      // The incoming route state reassembled from props — only for the debug
      // panel; outgoing changes are patches, never this snapshot.
      route_state(): PdfviewState {
        return {
          animation: this.animation,
          bleed: this.bleed,
          content: this.content,
          current_page: this.current_page,
          files: this.files,
          hidden_guides: this.hidden_guides,
          imposition_data: this.imposition_data,
          page_grouping: this.page_grouping,
          revision: this.revision,
          sidebar: this.sidebar,
          view_3d: this.view_3d,
          zoom: this.zoom,
        }
      },
    },
    data: {
      // The `revision` the form is currently synced to — set both when we push
      // a local edit and when we apply an inbound one. The next render's
      // `imposition_data` watcher compares the incoming `revision` prop against
      // this to recognize its own echo in O(1); see the watchers below.
      applied_revision: undefined as StringGUID | undefined,
      // True while an incoming route entry is being applied to the form node,
      // so the resulting form change is not pushed back out.
      applying: false,
      // Transient 3D camera preset — not routed. Changed via `set_camera` from
      // the book viewer's toggles and flowed back down as a prop.
      camera_mode: "front" as CameraMode,
      preset: undefined as Preset | undefined,
      hidden_fields: [] as string[],
      loading: true,
      node: undefined as ObjectForm<Record<string, unknown>> | undefined,
      // The file rows' own node — same seed record, its own form, and it
      // survives a preset swap untouched (the files don't change when the
      // imposition does).
      files_node: undefined as ObjectForm<Record<string, unknown>> | undefined,
      preset_id: default_preset as string,
      // The host's navigation adapter, captured from PdfviewRouter each
      // render — kept in data so methods and watchers can reach it too.
      router: {
        blank_href: () => undefined,
        navigate: Void,
        replace: Void,
      } as PdfviewRouterType,
      store: CreateFileCollectionStore(engine),
      // The plot's pages, one def each (see MediaDefs), shared by every
      // media showing them, created with the viewer as the plot context is
      images: CreateMediaImages(),
    },
    methods: {
      // The page filling a destination slot (see `slot_map`), and its raster on
      // demand. A method so whichever computed calls it (a media's slot, the 3D
      // textures) tracks the split as it streams, and nothing else does.
      request_media(filename: FileName, page_number: PageNumber): SlotMedia | undefined {
        const slot = this.slot_map[filename as string as DestinationID]?.[page_number]
        return slot && this.slot_media(slot)
      },
      // The media a plotted page's own shapes show, out of its own context
      // (see PlotMediaDef): each a page already at hand.
      media_for(context: FilePlottingContext): RequestMedia {
        return (filename, page_number) => {
          const entry = context.file_map?.[filename]?.[page_number]
          return (
            entry &&
            this.slot_media({
              filename,
              matrix: entry.matrix,
              page: FileSlotPage(entry.page),
              page_number,
            })
          )
        }
      },
      // A slot as a media shows it: the page's boxes and identity up front,
      // its raster and its def only once asked for. Every page is rastered
      // as the engine's `file_bleed` grows its trim, upload or generated.
      slot_media(slot: FileSlot): SlotMedia {
        const { page } = slot
        const bleed = engine.config.file_bleed
        const rasterize = (file: SinglePagePdf) => this.store.rasterize(file)
        const make = () => (page ? page.get_file().then(rasterize) : Promise.resolve(undefined))
        return {
          ...slot,
          guid: page?.guid,
          trim: page?.trim,
          bleed,
          raster: () => (page ? this.images.raster(page.guid, make) : make()),
          image: () =>
            page
              ? this.images.image(page.guid, { bleed, trim: page.trim }, page.get_file, rasterize)
              : Promise.resolve(undefined),
        }
      },
      default_destination(): string | undefined {
        const [id] = this.result_view?.file_destinations ?? []
        return id
      },
      // Add + process the file (split + background rasterize, resolving with the
      // page count), then add a form row; the node mutation alone is enough —
      // the `encoded_input` watcher pushes it out through the router.
      add_file(name: FileName, file: VirtualFile) {
        const entry = this.store.add(name, file)
        this.router.replace({ hidden_guides: ShowDestinations(this.hidden_guides) })
        // The row lands now, not when the split finishes: a big PDF takes
        // seconds to reveal its page count, and waiting for it left the panel
        // looking like the upload had been dropped. `range_for(0)` is the empty
        // range, so the row shows up with nothing in it — the store entry it
        // points at has no `page_count` yet, which is what FileRows reads as
        // "loading" (and what `fill_range` completes below).
        const rows = this.controller.file_rows_with(name, 0, this.default_destination())
        this.controller.file_key_node().validate(rows)
        // Resolves with 0 on failure, having put the error on the store entry —
        // `fill_range` then leaves the empty range alone and the row stays as
        // the error it is.
        return entry.process().then(count => this.controller.fill_range(name, count))
      },
      // Align the file collection with the form's current file rows and the
      // route's files: drop the stores for files no longer referenced, add +
      // process the newly referenced ones, and swap a store whose route file
      // changed content under the same name — a drop of `a.pdf` over `a.pdf`
      // wins, rather than leaving the old split on screen to be pushed back
      // over it. By content, not reference: the route is decoded afresh on
      // every change.
      sync_files() {
        const wanted = new Set(this.controller.file_names() as FileName[])
        for (const name of Keys(this.store.files) as FileName[]) {
          if (!wanted.has(name)) this.store.remove(name)
        }
        for (const name of wanted) {
          const file = this.files[name]
          if (!file) continue
          // A bare string is shorthand for a remote mirror (see
          // PdfviewState.files) — normalize it before it reaches the store.
          const resolved = StringQ(file) ? CreateRemoteFile({ mirrors: [file] }) : file
          const loaded = this.store.files[name]
          if (loaded && FileContentGUID(loaded.file) === FileContentGUID(resolved)) continue
          if (loaded) this.store.remove(name)
          void this.store
            .add(name, resolved)
            .process()
            .then(count => this.controller.fill_range(name, count))
        }
      },
      // The picker and a window drop are the same gesture from here down: both
      // go through `IngestFiles`, so the bytes land in the engine's own
      // persistent cache (surviving a reload) rather than behind a throwaway
      // blob URL, and both hand back the same {FileName: VirtualFile} record —
      // one row appended per entry.
      on_files(files: File[]) {
        return IngestFiles(engine, files).then(ingested =>
          WaitPromises(RecordMap((file, name) => this.add_file(name as FileName, file), ingested))
        )
      },
      set_camera(camera_mode: CameraMode) {
        this.camera_mode = camera_mode
      },
      // Just routes the page — each viewer already emits a valid in-range value
      // (its pager/arrows clamp to their own bounds, the plot reports scrolled
      // pages), so the host only rounds and de-dupes.
      set_page(page: number) {
        const current_page = Round(page)
        if (current_page === this.current_page) return
        this.router.replace({ current_page })
      },
      toggle_guide(id: string) {
        this.router.replace({
          hidden_guides: this.hidden_guides.includes(id)
            ? this.hidden_guides.filter((g: string) => g !== id)
            : [...this.hidden_guides, id],
        })
      },
      // Choice labels are baked into the form schema at build time, so a
      // language or preset change needs a fresh form, not a re-render.
      // Rebuilds against `seed` so current selections survive the swap.
      async load_form(seed: Record<string, unknown>) {
        const preset_id = (seed["preset"] as string | undefined) ?? this.preset_id
        const { preset, form, files_form } = await ResolvePreset(
          { preset: preset_id },
          { language: this.lang, ...form_config },
          presets
        )
        const node = CreateReactiveForm(form) as ObjectForm<Record<string, unknown>>
        const files_node = CreateReactiveForm(files_form) as ObjectForm<Record<string, unknown>>
        // The preset id always goes into the form input explicitly (never
        // elided for the default): every push then carries it, so re-applying
        // any history entry rebuilds an input identical to that entry and the
        // outbound watcher stays quiet — no spurious push after back/forward.
        // Every node is seeded from the same record; each keeps the keys it
        // owns and ignores the rest.
        const data = RecordMerge(preset.data, seed, { preset: preset.id })
        node.validate(data)
        files_node.validate(data)
        this.preset = preset
        this.hidden_fields = preset.hidden_fields ?? []
        // `node` last: it is what gates `controller`/`result`, so everything
        // they read must already be in place when it lands.
        this.files_node = files_node
        this.node = node
        this.preset_id = preset.id
        return preset
      },
      // Swap the active preset while preserving whatever files are already
      // loaded. Rebuilds the form locally (like `add_file`, the node change
      // alone is enough — the `encoded_input` watcher pushes it out), so the
      // single history entry carries the full new-preset input and its route
      // echo compares equal instead of triggering a second push.
      async switch_preset(id: string) {
        if (this.loading) return
        // The file rows come along in `input` like every other key — the new
        // preset's files node is seeded from it, same as the imposition one.
        const preserved = KeyTake(this.controller.input, ["file_key", "filename"])
        await this.load_form(RecordMerge({ preset: id }, preserved))
      },
    },
    mixins: [IDMixin, RootMixin] as const,
    async mounted() {
      // Deep-link support: seed from the preset defaults, then layer whatever
      // the incoming state already carried (e.g. a shared link, or a reload).
      // No other seeding happens here — a host that wants a pre-populated
      // viewer resolves that itself (see SeedPdfviewState) and navigates in
      // with the resulting state, same as any other deep link.
      await this.load_form(this.imposition_data)
      this.loading = false
    },
    props: PdfviewStateDefaults,
    render(children) {
      // Keeps every `useTranslation` call in this render pass (this
      // component's own JSX plus every Dispatch/DjLabel/etc. child it creates)
      // in sync with the host's current language — reading the computed here
      // is also what subscribes this render to the host's own signal.
      set_language(this.lang)
      const actions = useTranslationTyped("actions")
      const pdfview = useTranslationTyped("pdfview")
      this.router = useContext(PdfviewRouter)
      // The common header controls the host owns; each viewer renders them
      // verbatim (see ViewerHeader) alongside its own view-specific ones: the
      // 2D/3D switch in the corner over the rail, the host's action on the right.
      const header_corner = (
        <ToggleButton
          name="VIEW_3D"
          onPressedChange={() => this.router.replace({ view_3d: !this.view_3d })}
          pressed={this.view_3d}
          translate={() => pdfview.VIEW_3D()}
        />
      )
      const header_actions = action_button?.({
        data: this.form_data,
        state: this.route_state,
        result: this.result,
      })
      // The header spans the window above the sidebar (see ViewerSidebar), so
      // an open panel takes no room from it and every control stays. Each
      // viewer gets its finished left-group buttons and renders them verbatim.
      //
      // The stripe/normal layout toggle is shared by both renderers (row of
      // sheets in 2D, flat top-down in 3D). A lone page lays out identically
      // either way, so there's nothing to toggle.
      const stripe_control = this.pages.length > 1 && (
        <ToggleButton
          name="VIEW_STRIPE"
          onPressedChange={() =>
            this.router.replace({
              animation: this.animation === "normal" ? "stripe" : "normal",
            })
          }
          pressed={this.animation === "stripe"}
          translate={() => pdfview.VIEW_STRIPE()}
        />
      )
      const layout_controls = this.view_3d ? (
        <>
          <ToggleButton
            name="VIEW_FREE"
            onPressedChange={() => this.set_camera(this.camera_mode === "free" ? "front" : "free")}
            pressed={this.camera_mode === "free"}
            translate={() => pdfview.VIEW_FREE()}
          />
          {stripe_control}
        </>
      ) : (
        <>
          {/* Nothing to pair a lone page with. */}
          {this.pages.length > 1 && (
            <PageGroupingControl
              on_value={grouping => this.router.replace({ page_grouping: grouping })}
              value={this.page_grouping}
            />
          )}
          {stripe_control}
        </>
      )
      // Flat plot and 3D book are alternative renderers for the same state —
      // one shared prop bag, each component just ignores the other's extras and
      // each renders its own header from it.
      const PagesViewer = this.view_3d ? Book3dViewer : PlotViewer
      // Handed to whichever view is showing, which lays it out under its header.
      const sidebar = show_sidebar && (
        <ViewerSidebar
          cache={cache}
          content={this.content}
          debug={debug}
          fields={this.fields}
          files_node={this.files_node}
          hidden_fields={this.hidden_fields}
          informations={this.informations}
          node={this.node}
          page_counts={this.page_counts}
          sheet_math={this.sheet_math}
          on_content={content => this.router.replace({ content })}
          on_files={this.on_files}
          on_preset={this.switch_preset}
          preset_id={this.preset_id}
          presets={presets}
          selected={this.sidebar}
          state={this.route_state}
          store={this.store}
          view={this.result_view}
        >
          <Rail
            bottom={
              <>
                <ViewerPanels
                  align="bottom"
                  cache={cache}
                  debug={debug}
                  on_select={sidebar => this.router.replace({ sidebar })}
                  selected={this.sidebar}
                />
                {children && (
                  <div className="flex flex-col items-center gap-1 border-t pt-2">{children}</div>
                )}
              </>
            }
            top={
              <ViewerPanels
                align="top"
                cache={cache}
                debug={debug}
                on_select={sidebar => this.router.replace({ sidebar })}
                selected={this.sidebar}
              />
            }
          />
        </ViewerSidebar>
      )
      return (
        <SidebarProvider
          onOpenChange={open =>
            this.router.replace({ sidebar: open ? (this.sidebar ?? "files") : undefined })
          }
          open={!NullQ(this.sidebar)}
          style={{ "--sidebar-width": sidebar_width } as preact.JSX.CSSProperties}
        >
          {/* The active viewer renders its own header (see ViewerHeader); the
              empty state has none — nothing to act on yet, only the upload
              over the neutral gray plot backdrop (matches filecheck.tsx's
              rgb(240,240,240), tracked through dark mode by `bg-content` — the
              document-well token, which defaults to `--muted` and is what a
              translucent host thins). */}
          <SidebarInset className="h-svh overflow-hidden">
            {this.result && (
              <MediaDefs
                context={this.plot_context}
                images={this.images.images}
                media_for={this.media_for}
              />
            )}
            {this.result && this.pages.length ? (
              <PagesViewer
                active_guides={this.active_guides}
                animation={this.animation}
                bleed={this.bleed}
                camera_mode={this.camera_mode}
                context={this.plot_context}
                current_page={this.current_page}
                request_media={this.request_media}
                guides={this.guides}
                show_header={show_header}
                header_actions={header_actions}
                header_corner={header_corner}
                hidden_guides={this.hidden_guides}
                lang={this.lang}
                layout_controls={layout_controls}
                media_count={this.pages.length}
                on_current_page={this.set_page}
                on_toggle_bleed={() => this.router.replace({ bleed: !this.bleed })}
                on_toggle_guide={this.toggle_guide}
                on_zoom={(zoom: number) => this.router.replace({ zoom })}
                page_grouping={this.page_grouping}
                pages={this.pages}
                printable={this.printable}
                result={this.result}
                sidebar={sidebar}
                zoom={this.zoom}
              />
            ) : (
              <div className="flex h-full w-full">
                {sidebar}
                {/* Nothing to draw yet. Rather than say so in a sentence, put
                    the same control the files panel uses right where the
                    document would be — the empty state is the invitation. */}
                <div className="flex min-w-0 flex-1 items-center justify-center bg-content p-4 text-muted-foreground">
                  {this.loading ? (
                    actions.LOADING()
                  ) : (
                    // Roomier than the same card in the sidebar: there it is one
                    // row among many, here it is the only thing on the page.
                    <UploadCard className="w-full max-w-sm" on_files={this.on_files} />
                  )}
                </div>
              </div>
            )}
          </SidebarInset>
        </SidebarProvider>
      )
    },
    watch: {
      // Form → out. Fires on every node change, local or route-applied, so
      // the store reconcile runs unconditionally. A change caused by applying
      // inbound route state is silenced by `applying`; everything else is a
      // local edit and gets pushed, stamped with a fresh `revision`. The
      // route carries `revision` like any other field, so its echo (and every
      // later view-state push, which leaves `revision` untouched) is
      // recognized by the `imposition_data` watcher with a plain `===` — no
      // per-event re-encode of the whole input. Mount-time normalization
      // (deep-link expansion) replaces instead of pushing — reloading a link
      // must not grow history.
      // A file replaced under a name the rows already reference changes no
      // row, so `encoded_input` stays quiet — the route's files are watched
      // on their own.
      files() {
        if (this.node) this.sync_files()
      },
      encoded_input(next: string) {
        if (!this.node) return
        this.sync_files()
        if (this.applying || !next) return
        const revision = GuidToString(NewId())
        this.applied_revision = revision
        const patch = { files: this.loaded_files, imposition_data: this.controller.input, revision }
        if (this.loading) this.router.replace(patch)
        else this.router.navigate(patch)
      },
      // In → form. Every view-state change (page, zoom, sidebar, toggles)
      // round-trips the whole route back as fresh prop objects, so this fires
      // constantly — but view-state pushes never touch `revision`, and our own
      // document push comes back with the revision we just stamped. Either way
      // `revision` still matches the one the form is synced to, so we skip on
      // an O(1) compare instead of re-validating (which would rebuild the
      // result and the 3D book). Only a genuinely different document —
      // back/forward to another edit, external navigation, a hand-built link —
      // carries a revision we haven't applied and falls through. A different
      // preset needs a form rebuild, not a `validate`, since its schema
      // differs entirely (like the `lang` watcher).
      async imposition_data(next: Record<string, unknown>) {
        if (this.loading || !this.node || !this.files_node) return
        if (this.revision === this.applied_revision) return
        this.applied_revision = this.revision
        this.applying = true
        // No `ps` key means the default preset — external URLs may elide it,
        // and crossing a preset boundary must still trigger the rebuild.
        const preset = (next["preset"] as string | undefined) ?? default_preset
        if (preset !== this.preset_id) await this.load_form({ ...next, preset: preset })
        else {
          this.node.validate(next)
          this.files_node.validate(next)
        }
        // Applying inbound state changes the form, which re-fires the
        // `encoded_input` watcher; `applying` suppresses that echo so an
        // expanded deep link isn't pushed back as a fresh entry. The queued
        // effect runs after this handler's synchronous portion, so yield once
        // to keep the flag raised until it has flushed.
        await Promise.resolve()
        this.applying = false
      },
      // Host-driven language change: rebuild the form (choice labels are
      // baked in at build time) against whatever the current node holds, so
      // field selections carry over and only their displayed text changes.
      async lang() {
        if (this.loading || !this.node) return
        await this.load_form(this.controller.input)
      },
    },
  })
}
